import { useEffect, useState, useCallback, useRef } from 'react';
import { useScrollHeader } from '../utils/hooks';
import api from '../services/api';
import MathText from '../components/MathText';
import SEO from '../components/SEO';
import { canUserManageContent } from '../utils/rbac';
import ThemeSwitcher from '../components/ThemeSwitcher';

/* ─── Helpers ─────────────────────────────────── */
const pct = (a, b) => (b === 0 ? 0 : Math.round((a / b) * 100));

/* ─── Main Component ──────────────────────────── */
const TakeTestPage = ({ examId, onBack, onGoToBrowse, theme, onThemeChange, onTestActiveChange, user }) => {
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isLoaded, setIsLoaded] = useState(false);
  const stateKey = `studymate_active_test_${examId}`;

  // Test-taking state
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(null);
  const [solutionPopup, setSolutionPopup] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [timeExpired, setTimeExpired] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [viewMode, setViewMode] = useState('single'); // 'single' or 'all'
  const [isHeaderPinned, setIsHeaderPinned] = useState(true);
  const [isHeaderOpen, setIsHeaderOpen] = useState(true);
  const lastScrollY = useRef(0);



  const shouldHeaderShow = isHeaderOpen || showExitConfirm || showConfirm || submitted || isHeaderPinned;
  const skipProtection = useRef(false);
  const answersRef = useRef({});
  const timerRef = useRef(null);
  const touchStartRef = useRef({ x: null, y: null });
  const touchEndRef = useRef({ x: null, y: null });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 720);
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(window.innerWidth <= 1024);
  // Bottom Sheet dragging variables
  const [panelHeight, setPanelHeight] = useState(window.innerHeight * 0.5);
  const isDraggingRef = useRef(false);
  const dragStartYRef = useRef(0);
  const dragStartHeightRef = useRef(0);
  const currentHeightRef = useRef(window.innerHeight * 0.5);
  const dragStartTimeRef = useRef(0);

  const handleDragMove = useCallback((e) => {
    if (!isDraggingRef.current) return;
    const clientY = e.clientY || e.touches?.[0]?.clientY || 0;
    const deltaY = clientY - dragStartYRef.current;
    const newHeight = Math.max(100, Math.min(window.innerHeight, dragStartHeightRef.current - deltaY));
    setPanelHeight(newHeight);
    currentHeightRef.current = newHeight;
  }, []);

  const handleDragEnd = useCallback(() => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    document.body.style.userSelect = '';

    window.removeEventListener('mousemove', handleDragMove);
    window.removeEventListener('mouseup', handleDragEnd);
    window.removeEventListener('touchmove', handleDragMove);
    window.removeEventListener('touchend', handleDragEnd);

    const dragDuration = Date.now() - dragStartTimeRef.current;
    const distanceMovedDown = dragStartHeightRef.current - currentHeightRef.current;
    
    // Calculate downward drag velocity (pixels per millisecond)
    const velocity = dragDuration > 0 ? distanceMovedDown / dragDuration : 0;

    // Swift downward swipe (velocity > 0.5px/ms and dragged down > 30px) closes it
    const isSwipeDown = velocity > 0.5 && distanceMovedDown > 30;

    if (isSwipeDown || currentHeightRef.current < window.innerHeight * 0.25) {
      setShowMap(false);
      setPanelHeight(window.innerHeight * 0.5);
      currentHeightRef.current = window.innerHeight * 0.5;
    }
  }, [handleDragMove]);

  const handleDragStart = (e) => {
    isDraggingRef.current = true;
    const clientY = e.clientY || e.touches?.[0]?.clientY || 0;
    dragStartYRef.current = clientY;
    dragStartHeightRef.current = panelHeight;
    dragStartTimeRef.current = Date.now();
    document.body.style.userSelect = 'none';

    window.addEventListener('mousemove', handleDragMove);
    window.addEventListener('mouseup', handleDragEnd);
    window.addEventListener('touchmove', handleDragMove, { passive: false });
    window.addEventListener('touchend', handleDragEnd);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 720);
      setIsMobileOrTablet(window.innerWidth <= 1024);
      if (showMap && !isDraggingRef.current) {
        setPanelHeight(window.innerHeight * 0.5);
        currentHeightRef.current = window.innerHeight * 0.5;
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [showMap]);

  useEffect(() => {
    if (showMap) {
      setPanelHeight(window.innerHeight * 0.5);
      currentHeightRef.current = window.innerHeight * 0.5;
      
      // Give the map time to render, then smoothly scroll only the map container
      setTimeout(() => {
        const activeDot = document.querySelector('.test-nav-panel .dot-current');
        const scrollContainer = document.querySelector('.test-nav-panel > div:last-child');
        
        if (activeDot && scrollContainer) {
          const dotPos = activeDot.offsetTop;
          const containerHalf = scrollContainer.clientHeight / 2;
          scrollContainer.scrollTo({
            top: Math.max(0, dotPos - containerHalf),
            behavior: 'smooth'
          });
        }
      }, 50);
    }
  }, [showMap]);

  const panelStyle = isMobileOrTablet ? { height: `${panelHeight}px` } : {};

  useEffect(() => {
    setIsLoaded(false);
    setLoading(true);
    api.get(`/exams/${examId}`)
      .then(r => { 
        const fetchedExam = r.data;
        if (fetchedExam.preferPdf) {
          api.get(`/files/presign-all/${examId}?type=exam`)
            .then(res => {
              fetchedExam.presignedFiles = res.data.files;
              setExam(fetchedExam); 
              setLoading(false); 
            })
            .catch(() => {
              // fallback if presign fails
              setExam(fetchedExam); 
              setLoading(false);
            });
        } else {
          setExam(fetchedExam); 
          setLoading(false); 
        }
      })
      .catch(err => { setError(err.response?.data?.error || 'Failed to load exam'); setLoading(false); });
  }, [examId]);

  const questions = exam?.questions || [];
  const total = questions.length;

  // Keep answersRef in sync so the timer auto-submit can access latest answers
  useEffect(() => { answersRef.current = answers; }, [answers]);

  // Load saved state once exam is loaded
  useEffect(() => {
    if (!exam || isLoaded) return;

    // Load saved state
    const saved = localStorage.getItem(stateKey);
    let loadedAnswers = {};
    let loadedCurrent = 0;
    let loadedSubmitted = false;
    let loadedScore = null;

    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed) {
          loadedAnswers = parsed.answers || {};
          loadedCurrent = parsed.current || 0;
          loadedSubmitted = !!parsed.submitted;
          loadedScore = parsed.score !== undefined ? parsed.score : null;

          setAnswers(loadedAnswers);
          setCurrent(loadedCurrent);
          if (loadedSubmitted) {
            setSubmitted(true);
            setScore(loadedScore);
          }
        }
      } catch (e) {
        console.error('Failed to parse saved test state:', e);
      }
    }

    setIsLoaded(true);
  }, [exam, examId, isLoaded]);

  // Manage countdown/timer once state is loaded
  useEffect(() => {
    if (!exam || !isLoaded || !exam.timeLimitMinutes || submitted) return;

    const saved = localStorage.getItem(stateKey);
    let endTime = null;
    let loadedAnswers = {};
    let loadedCurrent = 0;

    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        endTime = parsed?.endTime;
        loadedAnswers = parsed?.answers || {};
        loadedCurrent = parsed?.current || 0;
      } catch (e) {}
    }

    if (!endTime) {
      endTime = Date.now() + exam.timeLimitMinutes * 60 * 1000;
      // Save initial state immediately so a quick refresh retains it
      const initialSave = {
        answers: loadedAnswers,
        current: loadedCurrent,
        endTime,
        submitted: false,
        score: null
      };
      localStorage.setItem(stateKey, JSON.stringify(initialSave));
    }

    const updateTimer = () => {
      const now = Date.now();
      const left = Math.max(0, Math.round((endTime - now) / 1000));
      setSecondsLeft(left);
      if (left <= 0) {
        clearInterval(timerRef.current);
        setTimeExpired(true);
      }
    };

    updateTimer(); // initial run
    timerRef.current = setInterval(updateTimer, 1000);

    return () => clearInterval(timerRef.current);
  }, [exam, examId, isLoaded, submitted]);

  // Save state to localStorage on answers, current question, or submitted status changes
  useEffect(() => {
    if (!exam || !isLoaded) return;

    const saved = localStorage.getItem(stateKey);
    let endTime = null;
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        endTime = parsed?.endTime;
      } catch (e) {}
    }
    if (!endTime && exam.timeLimitMinutes) {
      endTime = Date.now() + exam.timeLimitMinutes * 60 * 1000;
    }

    const stateToSave = {
      answers,
      current,
      endTime,
      submitted,
      score
    };
    localStorage.setItem(stateKey, JSON.stringify(stateToSave));
  }, [answers, current, exam, submitted, score, examId, isLoaded]);

  // ── Exit Protection ──
  useEffect(() => {
    if (loading || !exam || submitted || exam.preferPdf || total === 0) return;

    // Defer pushing dummy history state until first user interaction (critical for iOS/WebKit to allow history navigation protection)
    const pushDummyState = () => {
      if (!window.history.state || !window.history.state.isDummy) {
        window.history.pushState({ isDummy: true }, '', window.location.href);
      }
      // Remove listeners once dummy state is successfully pushed
      document.removeEventListener('click', pushDummyState);
      document.removeEventListener('touchstart', pushDummyState);
      document.removeEventListener('keydown', pushDummyState);
    };

    document.addEventListener('click', pushDummyState);
    document.addEventListener('touchstart', pushDummyState, { passive: true });
    document.addEventListener('keydown', pushDummyState);

    const handleBeforeUnload = (e) => {
      if (skipProtection.current || submitted) return;
      e.preventDefault();
      const message = 'Are you sure you want to leave? Your progress will be lost.';
      e.returnValue = message;
      return message;
    };

    const handlePopState = (e) => {
      if (skipProtection.current || submitted) return;

      // The user hit back, so the dummy was popped.
      // We push it back to stay on the page and show the modal.
      window.history.pushState({ isDummy: true }, '', window.location.href);
      setShowExitConfirm(true);
    };

    const handleContextMenu = (e) => {
      e.preventDefault();
    };

    const handleCopy = (e) => {
      e.preventDefault();
    };

    const handleCut = (e) => {
      e.preventDefault();
    };

    const handleKeyDown = (e) => {
      // F5 or Ctrl+F5 (Refresh)
      if (e.key === 'F5' || e.keyCode === 116) {
        e.preventDefault();
        return false;
      }
      // Ctrl+R or Cmd+R (Refresh)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'r' || e.key === 'R' || e.keyCode === 82)) {
        e.preventDefault();
        return false;
      }
      // Ctrl+C or Cmd+C (Copy)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'C' || e.keyCode === 67)) {
        e.preventDefault();
        return false;
      }
      // Ctrl+X or Cmd+X (Cut)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'x' || e.key === 'X' || e.keyCode === 88)) {
        e.preventDefault();
        return false;
      }
      // Ctrl+U or Cmd+U (View Source)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'u' || e.key === 'U' || e.keyCode === 85)) {
        e.preventDefault();
        return false;
      }
    };

    // iOS Swipe Back Prevention
    const handleLeftTouchStart = (e) => {
      const touch = e.touches[0];
      // If the touch starts in the leftmost 20px (edge of the screen), prevent it.
      // This blocks the native iOS swipe-to-go-back gesture before the OS initiates it.
      if (touch.clientX < 20) {
        e.preventDefault();
      }
    };

    const handleTriggerExitConfirm = () => {
      setShowExitConfirm(true);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('contextmenu', handleContextMenu);
    window.addEventListener('copy', handleCopy);
    window.addEventListener('cut', handleCut);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('touchstart', handleLeftTouchStart, { passive: false });
    window.addEventListener('trigger-test-exit-confirm', handleTriggerExitConfirm);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('contextmenu', handleContextMenu);
      window.removeEventListener('copy', handleCopy);
      window.removeEventListener('cut', handleCut);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('touchstart', handleLeftTouchStart);
      window.removeEventListener('trigger-test-exit-confirm', handleTriggerExitConfirm);

      document.removeEventListener('click', pushDummyState);
      document.removeEventListener('touchstart', pushDummyState);
      document.removeEventListener('keydown', pushDummyState);
    };
  }, [submitted, loading, exam?.preferPdf, total]);

  const confirmExit = () => {
    skipProtection.current = true;
    setShowExitConfirm(false);
    if (onTestActiveChange) {
      onTestActiveChange(false);
    }
    document.body.classList.remove('test-active-body');
    document.documentElement.classList.remove('test-active-body');
    localStorage.removeItem(stateKey);

    // Determine how many steps to go back based on our dummy state
    if (window.history.state && window.history.state.isDummy) {
      window.history.go(-2);
    } else {
      window.history.back();
    }
  };

  const handleResultsBack = () => {
    localStorage.removeItem(stateKey);
    onBack();
  };

  useEffect(() => {
    if (!loading && exam && !exam.preferPdf && questions.length > 0 && !submitted) {
      document.body.classList.add('test-active-body');
      document.documentElement.classList.add('test-active-body');
      if (onTestActiveChange) {
        onTestActiveChange(true);
      }
    } else {
      document.body.classList.remove('test-active-body');
      document.documentElement.classList.remove('test-active-body');
      if (onTestActiveChange) {
        onTestActiveChange(false);
      }
    }
    return () => {
      document.body.classList.remove('test-active-body');
      document.documentElement.classList.remove('test-active-body');
      if (onTestActiveChange) {
        onTestActiveChange(false);
      }
    };
  }, [loading, exam, submitted, questions.length, onTestActiveChange]);

  /* ── Answer helpers ── */
  const toggleMcqAnswer = useCallback((qIdx, option) => {
    setAnswers(prev => {
      const current = Array.isArray(prev[qIdx]) ? prev[qIdx] : [];
      return {
        ...prev,
        [qIdx]: current.includes(option)
          ? current.filter(a => a !== option)
          : [...current, option],
      };
    });
  }, []);

  const selectSingleMcqAnswer = useCallback((qIdx, option) => {
    setAnswers(prev => ({ ...prev, [qIdx]: [option] }));
  }, []);

  const setOpenAnswer = useCallback((qIdx, value) => {
    setAnswers(prev => ({ ...prev, [qIdx]: value }));
  }, []);

  /* ── Navigation ── */
  const goNext = () => setCurrent(c => Math.min(c + 1, exam.questions.length - 1));
  const goPrev = () => setCurrent(c => Math.max(c - 1, 0));

  const toggleViewMode = () => {
    setViewMode(prev => {
      const next = prev === 'single' ? 'all' : 'single';
      if (next === 'all') {
        setTimeout(() => {
          const prefix = submitted ? 'review-q' : 'test-q';
          const el = document.getElementById(`${prefix}-${current}`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 50);
      }
      return next;
    });
  };

  const handleTouchStart = (e) => {
    touchStartRef.current = {
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    };
    touchEndRef.current = { x: null, y: null };
  };

  const handleTouchMove = (e) => {
    const currentX = e.targetTouches[0].clientX;
    const currentY = e.targetTouches[0].clientY;
    touchEndRef.current = { x: currentX, y: currentY };

    const startX = touchStartRef.current.x;
    const startY = touchStartRef.current.y;

    if (startX !== null && startY !== null) {
      const diffX = startX - currentX;
      const diffY = startY - currentY;
      const minSwipeDistance = 40;

      // Check if vertical or horizontal swipe is dominant
      if (Math.abs(diffY) > Math.abs(diffX)) {
        if (diffY < -minSwipeDistance) {
          // Dragging down -> show header immediately!
          setIsHeaderOpen(true);
        } else if (diffY > minSwipeDistance) {
          // Dragging up -> hide header immediately!
          setIsHeaderOpen(false);
        }
      }
    }
  };

  const handleTouchEnd = () => {
    if (viewMode !== 'single') return;
    const startX = touchStartRef.current.x;
    const startY = touchStartRef.current.y;
    const endX = touchEndRef.current.x;
    const endY = touchEndRef.current.y;

    if (startX === null || endX === null || startY === null || endY === null) return;
    const diffX = startX - endX;
    const diffY = startY - endY;
    const minSwipeDistance = 50;

    // Check if horizontal swipe is dominant
    if (Math.abs(diffX) >= Math.abs(diffY)) {
      if (diffX > minSwipeDistance) goNext();
      if (diffX < -minSwipeDistance) goPrev();
    }
  };

  // Auto-submit when timer expires
  useEffect(() => {
    if (!timeExpired || submitted) return;
    // Calculate marks-based score from latest answers
    let totalScore = 0;
    exam.questions.forEach((q, idx) => {
      if (q.type === 'mcq') {
        const given = Array.isArray(answersRef.current[idx]) ? [...answersRef.current[idx]].sort() : [];
        const expected = [...(q.correctAnswers || [])].sort();
        const isCorrect = JSON.stringify(given) === JSON.stringify(expected);
        const hasAttempted = given.length > 0;
        if (isCorrect) {
          totalScore += (q.marks ?? 1);
        } else if (hasAttempted) {
          totalScore -= (q.negativeMarks ?? 0);
        }
      }
    });
    setScore(Math.max(0, totalScore));
    if (window.history.state && window.history.state.isDummy) {
      skipProtection.current = true;
      window.history.back();
    }
    setSubmitted(true);
    setShowConfirm(false);
    window.scrollTo(0, 0);
  }, [timeExpired]);

  /* ── Submit ── */
  const triggerSubmit = () => setShowConfirm(true);

  const confirmSubmit = () => {
    clearInterval(timerRef.current);
    let totalScore = 0;
    exam.questions.forEach((q, idx) => {
      if (q.type === 'mcq') {
        const given = Array.isArray(answers[idx]) ? [...answers[idx]].sort() : [];
        const expected = [...(q.correctAnswers || [])].sort();
        const isCorrect = JSON.stringify(given) === JSON.stringify(expected);
        const hasAttempted = given.length > 0;
        if (isCorrect) {
          totalScore += (q.marks ?? 1);
        } else if (hasAttempted) {
          totalScore -= (q.negativeMarks ?? 0);
        }
      }
    });
    setScore(Math.max(0, totalScore));
    if (window.history.state && window.history.state.isDummy) {
      skipProtection.current = true;
      window.history.back();
    }
    setSubmitted(true);
    setShowConfirm(false);
    window.scrollTo(0, 0);
  };

  /* ── Loading / Error ── */
  if (loading) return (
    <div className="page-card" style={{ textAlign: 'center', padding: '60px' }}>
      <div className="spinner" />
      <p style={{ color: 'var(--text-muted)', marginTop: '16px' }}>Loading exam…</p>
    </div>
  );

  if (error) return (
    <div className="page-card">
      <button className="secondary" onClick={onBack}>← Back</button>
      <div className="error-message" style={{ marginTop: '16px' }}>{error}</div>
    </div>
  );

  if (!exam) return null;

  const publicFiles = exam.presignedFiles 
    ? exam.presignedFiles.map(f => ({ url: f.presignedUrl, name: f.name }))
    : (exam.files && exam.files.length > 0 
        ? exam.files.filter(f => f.visibility === 'public')
        : (exam.fileUrl ? [{ url: exam.fileUrl, name: 'Paper' }] : []));

  // 1. PDF-only view
  if (exam.preferPdf && publicFiles.length > 0) {
    return (
      <div className="test-page-container" style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <SEO title={`View Paper: ${exam.title}`} description={`Viewing the PDF version of ${exam.title}.`} />
        <header className="test-header" style={{ padding: '12px 32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button className="secondary submit-btn-small" type="button" onClick={onBack}>
              <i className="fas fa-arrow-left" /> Back
            </button>
            <strong style={{ fontSize: '1rem' }}><MathText key={exam.title} text={exam.title} /></strong>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {canUserManageContent(user, exam) && publicFiles.map((fileObj, idx) => (
              <a 
                key={idx} 
                href={`/document/${exam._id}`} 
                target="_blank" 
                rel="noreferrer" 
                className="premium-icon-btn" 
                title={`Open ${publicFiles.length > 1 ? fileObj.name : 'in New Tab'}`}
                style={{ color: 'var(--text-main)', textDecoration: 'none' }}
              >
                <i className="fas fa-external-link-alt" />
              </a>
            ))}
          </div>
        </header>
        <div style={{ flex: 1, background: '#525659', display: 'flex', flexDirection: publicFiles.length > 1 ? 'row' : 'column', gap: '8px', padding: '8px' }}>
          {publicFiles.map((fileObj, idx) => (
            <div key={idx} style={{ flex: 1, height: '100%' }}>
              <iframe 
                src={`${fileObj.url}#toolbar=0`} 
                title={fileObj.name || exam.title}
                style={{ width: '100%', height: '100%', border: 'none', borderRadius: '4px' }}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // 2. Empty questions fallback
  if (total === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh', width: '100%' }}>
        <div className="page-card" style={{ textAlign: 'center', padding: '60px', maxWidth: '500px', width: '90%' }}>
          <SEO title={`Exam: ${exam.title}`} />
          <i className="fas fa-exclamation-circle fa-3x" style={{ color: 'var(--warning)', marginBottom: '20px' }} />
          <h3>No Questions Available</h3>
          <p style={{ color: 'var(--text-muted)', margin: '16px 0 24px' }}>This exam does not have any interactive questions yet.</p>
          <button className="primary" onClick={onBack} style={{ width: 'auto', margin: '0 auto' }}>Go Back</button>
        </div>
      </div>
    );
  }

  /* ────────────────────────────────────────────
     RESULTS SCREEN
  ──────────────────────────────────────────── */
  if (submitted) {
    const mcqQuestions = questions.filter(q => q.type === 'mcq');
    // Total possible marks = sum of each MCQ question's marks value
    const totalPossibleMarks = mcqQuestions.reduce((sum, q) => sum + (q.marks ?? 1), 0);
    const scorePct = pct(score, totalPossibleMarks || 1);

    const q = questions[current];
    const given = answers[current];
    const givenArr = Array.isArray(given) ? given : [];
    const expected = q?.correctAnswers || [];
    const isCorrect = q?.type === 'mcq' &&
      JSON.stringify([...givenArr].sort()) === JSON.stringify([...expected].sort());

    return (
      <div 
        className={`test-page-container ${shouldHeaderShow ? 'header-visible' : ''}`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        style={{ margin: '0 auto' }}
      >
        <SEO title={`Results: ${exam.title}`} description={`View your test results for ${exam.title}. Your score: ${scorePct}%.`} />
        {/* ── Sticky Header ── */}
        <header className="test-header">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 32px', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <button className="secondary submit-btn-small" type="button" onClick={handleResultsBack}>
                <i className="fas fa-arrow-left" /> Back to Directory
              </button>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                <strong style={{ fontSize: '1rem' }}><MathText key={exam.title} text={exam.title} /></strong>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button 
                className="secondary submit-btn-small" 
                onClick={toggleViewMode}
                style={{ width: 'auto' }}
              >
                <i className={`fas ${viewMode === 'single' ? 'fa-list' : 'fa-pager'}`} style={{ marginRight: '6px' }} />
                {viewMode === 'single' ? 'Show All' : 'Show One by One'}
              </button>

              <div className={`header-timer ${scorePct >= 70 ? 'timer-success' : 'timer-danger'}`} style={{ border: 'none', background: scorePct >= 70 ? '#10b981' : '#ef4444', color: 'white' }}>
                Score: {score}/{totalPossibleMarks} marks
              </div>
              
              <ThemeSwitcher 
                theme={theme} 
                onThemeChange={onThemeChange} 
                compact={true} 
                showLabel={false} 
              />
            </div>
          </div>
        </header>

        <div className="test-body" id="test-body-area">

          {viewMode === 'single' ? (
            /* Main Content Area with Side Nav */
            <div className="test-body-layout">
              <button
                type="button"
                className="nav-side-btn"
                onClick={goPrev}
                disabled={current === 0}
                title="Previous Question"
              >
                <i className="fas fa-chevron-left" />
              </button>

              <div className={`test-question-card review-card ${q?.type === 'mcq' ? (isCorrect ? 'review-correct' : 'review-wrong') : 'review-open'}`} style={{ flex: 1, maxWidth: '1000px', margin: '0 auto', minHeight: '300px', padding: '24px' }}>
                <div className="test-question-header" style={{ display: 'flex', gap: isMobile ? '8px' : '16px', marginBottom: '16px', alignItems: 'flex-start' }}>
                  {!isMobile && (
                    <span className="question-number" style={{ flexShrink: 0, marginTop: '2px' }}>{current + 1}</span>
                  )}
                  <div className="test-question-text" style={{ flex: 1, margin: 0, fontSize: '1.25rem', lineHeight: '1.6', color: 'var(--text-main)', display: 'flex', flexDirection: 'column' }}>
                    <MathText key={q?.text} text={isMobile ? `${current + 1}. ${q?.text}` : q?.text} />
                    {q?.imageUrl && (
                      <div style={{ width: '100%', marginTop: '16px', textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
                        <img 
                          src={q.imageUrl} 
                          alt="Question" 
                          style={{ maxWidth: '100%', maxHeight: '350px', borderRadius: '12px', border: '1px solid var(--border-light)', boxShadow: 'var(--shadow-sm)', cursor: 'zoom-in' }} 
                          onClick={() => window.open(q.imageUrl, '_blank')}
                        />
                      </div>
                    )}
                  </div>
                  
                  {q?.type === 'mcq' && (
                    <span className={`review-verdict ${isCorrect ? 'verdict-correct' : 'verdict-wrong'}`} style={{ flexShrink: 0, fontSize: '0.825rem', fontWeight: 700 }}>
                      {isCorrect ? '✓ Correct' : '✗ Incorrect'}
                    </span>
                  )}
                  {q?.type === 'normal' && (
                    <span className="review-verdict verdict-open" style={{ flexShrink: 0, fontSize: '0.825rem', fontWeight: 700 }}>Manually marked</span>
                  )}
                </div>

                {q?.type === 'mcq' ? (
                  <ul style={{ margin: '24px 0', display: 'grid', gap: '12px', listStyleType: 'none', padding: 0 }}>
                    {(q.options || []).map((o, i) => {
                      const optImg = q.optionImages?.[i];
                      const optIdentifier = o?.trim() || optImg;
                      if (!optIdentifier) return null;

                      const wasChosen = givenArr.includes(optIdentifier);
                      const isExpected = expected.includes(optIdentifier);
                      const isSingle = q.correctAnswers?.length === 1;
                      const isWrongChoice = wasChosen && !isExpected;
                      const optBorderColor = isExpected ? 'var(--success)' : isWrongChoice ? 'var(--danger)' : 'var(--border-light)';
                      const optBackground = isExpected ? 'rgba(16, 185, 129, 0.05)' : isWrongChoice ? 'rgba(239, 68, 68, 0.05)' : 'var(--bg-card)';
                      const optColor = isExpected ? 'var(--success)' : isWrongChoice ? 'var(--danger)' : 'var(--text-main)';

                      return (
                        <li key={i} className={`test-option ${wasChosen ? 'test-option-selected' : ''}`} style={{
                          cursor: 'default',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px 16px'
                        }}>
                          <div style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: isSingle ? '50%' : '6px',
                            border: '2px solid',
                            borderColor: isExpected ? 'var(--success)' : isWrongChoice ? 'var(--danger)' : 'var(--border-light)',
                            background: isExpected ? 'var(--success)' : isWrongChoice ? 'var(--danger)' : 'var(--bg-card)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '0.8rem',
                            flexShrink: 0
                          }}>
                            {isExpected && <i className="fas fa-check" style={{ fontSize: '0.8rem' }} />}
                            {isWrongChoice && <i className="fas fa-times" style={{ fontSize: '0.8rem' }} />}
                          </div>
                          <div className="test-option-text" style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
                            {o?.trim() && <MathText key={o} text={o} />}
                            {optImg && (
                              <div style={{ marginTop: '4px' }}>
                                <img 
                                  src={optImg} 
                                  alt={`Option choice`} 
                                  style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '8px', border: '1px solid var(--border-light)', marginTop: '4px', cursor: 'zoom-in' }} 
                                  onClick={() => window.open(optImg, '_blank')}
                                />
                              </div>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <div style={{ background: 'var(--bg-main)', borderRadius: '8px', padding: '20px', fontSize: '1rem', color: 'var(--text-main)', border: '1px solid var(--border-light)', display: 'grid', gap: '16px', marginTop: '24px' }}>
                    <div>
                      <strong style={{ display: 'block', marginBottom: '8px' }}>Your answer:</strong>
                      {given ? <MathText key={given} text={given} /> : <em style={{ color: 'var(--text-muted)' }}>No answer provided</em>}
                    </div>
                    {expected.length > 0 && (
                      <div style={{ borderTop: '1px solid var(--border-light)', paddingTop: '16px' }}>
                        <strong style={{ color: 'var(--success)', display: 'block', marginBottom: '8px' }}>Correct Answer(s):</strong>
                        <ul style={{ margin: '4px 0 0', paddingLeft: '18px', color: 'var(--success)' }}>
                          {expected.map((a, i) => <li key={i}><MathText key={a} text={a} /></li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {q.solution && (
                  <div style={{ background: 'var(--bg-main)', borderRadius: '8px', padding: '20px', border: '1px solid var(--border-light)', marginTop: '20px' }}>
                    <strong style={{ display: 'block', marginBottom: '8px', color: 'var(--text-main)' }}>Explanation/Solution:</strong>
                    <div style={{ fontSize: '1rem', lineHeight: '1.6', color: 'var(--text-main)' }}>
                      <MathText text={q.solution} />
                    </div>
                  </div>
                )}

                <div style={{ borderTop: '1px solid var(--border-light)', paddingTop: '16px', marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
                  <a
                    href={`/question/${examId}/${q._id}`}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => {
                      if (!e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey) {
                        e.preventDefault();
                        setSolutionPopup({ question: q, index: current });
                      }
                    }}
                    className="submit-btn-small"
                    style={{ background: 'var(--primary)', color: 'white', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                  >
                    <i className="fas fa-lightbulb" /> View Solution & Notes
                  </a>
                </div>
              </div>

              <button
                type="button"
                className="nav-side-btn"
                onClick={goNext}
                disabled={current === total - 1}
                title="Next Question"
              >
                <i className="fas fa-chevron-right" />
              </button>
            </div>
          ) : (
            /* Scrollable list of all questions */
            <div style={{ maxWidth: '1000px', margin: '0 auto 40px', padding: '0 20px', display: 'grid', gap: '20px' }}>
              {questions.map((qItem, idx) => {
                const g = answers[idx];
                const gArr = Array.isArray(g) ? g : [];
                const exp = qItem.correctAnswers || [];
                const correct = qItem.type === 'mcq' &&
                  JSON.stringify([...gArr].sort()) === JSON.stringify([...exp].sort());

                return (
                  <div 
                    key={idx} 
                    id={`review-q-${idx}`}
                    className={`test-question-card review-card ${qItem.type === 'mcq' ? (correct ? 'review-correct' : 'review-wrong') : 'review-open'}`}
                    style={{ margin: 0, padding: '24px' }}
                  >
                    <div className="test-question-header" style={{ display: 'flex', gap: isMobile ? '8px' : '16px', marginBottom: '16px', alignItems: 'flex-start' }}>
                      {!isMobile && (
                        <span className="question-number" style={{ flexShrink: 0, marginTop: '2px' }}>{idx + 1}</span>
                      )}
                      <div className="test-question-text" style={{ flex: 1, margin: 0, fontSize: '1.25rem', lineHeight: '1.6', color: 'var(--text-main)', display: 'flex', flexDirection: 'column' }}>
                        <MathText key={qItem.text} text={isMobile ? `${idx + 1}. ${qItem.text}` : qItem.text} />
                        {qItem.imageUrl && (
                          <div style={{ width: '100%', marginTop: '16px', textAlign: 'center' }}>
                            <img 
                              src={qItem.imageUrl} 
                              alt="Question" 
                              style={{ maxWidth: '100%', maxHeight: '350px', borderRadius: '12px', border: '1px solid var(--border-light)', boxShadow: 'var(--shadow-sm)' }} 
                            />
                          </div>
                        )}
                      </div>
                      
                      {qItem.type === 'mcq' && (
                        <span className={`review-verdict ${correct ? 'verdict-correct' : 'verdict-wrong'}`} style={{ flexShrink: 0, fontSize: '0.825rem', fontWeight: 700 }}>
                          {correct ? '✓ Correct' : '✗ Incorrect'}
                        </span>
                      )}
                      {qItem.type === 'normal' && (
                        <span className="review-verdict verdict-open" style={{ flexShrink: 0, fontSize: '0.825rem', fontWeight: 700 }}>Manually marked</span>
                      )}
                    </div>

                    {qItem.type === 'mcq' ? (
                      <ul style={{ margin: '24px 0', display: 'grid', gap: '12px', listStyleType: 'none', padding: 0 }}>
                        {(qItem.options || []).map((o, i) => {
                          const optImg = qItem.optionImages?.[i];
                          const optIdentifier = o?.trim() || optImg;
                          if (!optIdentifier) return null;

                          const chosen = gArr.includes(optIdentifier);
                          const expectedChoice = exp.includes(optIdentifier);
                          const singleChoice = qItem.correctAnswers?.length === 1;
                          const isWrongSelection = chosen && !expectedChoice;
                          const optBorderColor = expectedChoice ? 'var(--success)' : isWrongSelection ? 'var(--danger)' : 'var(--border-light)';
                          const optBackground = expectedChoice ? 'rgba(16, 185, 129, 0.05)' : isWrongSelection ? 'rgba(239, 68, 68, 0.05)' : 'var(--bg-card)';
                          const optColor = expectedChoice ? 'var(--success)' : isWrongSelection ? 'var(--danger)' : 'var(--text-main)';

                          return (
                            <li key={i} className={`test-option ${chosen ? 'test-option-selected' : ''}`} style={{
                              cursor: 'default',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              padding: '12px 16px'
                            }}>
                              <div style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: singleChoice ? '50%' : '6px',
                                border: '2px solid',
                                borderColor: expectedChoice ? 'var(--success)' : isWrongSelection ? 'var(--danger)' : 'var(--border-light)',
                                background: expectedChoice ? 'var(--success)' : isWrongSelection ? 'var(--danger)' : 'var(--bg-card)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '0.8rem',
                                flexShrink: 0
                              }}>
                                {expectedChoice && <i className="fas fa-check" style={{ fontSize: '0.8rem' }} />}
                                {isWrongSelection && <i className="fas fa-times" style={{ fontSize: '0.8rem' }} />}
                              </div>
                              <div className="test-option-text" style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
                                {o?.trim() && <MathText key={o} text={o} />}
                                {optImg && (
                                  <div style={{ marginTop: '4px' }}>
                                    <img 
                                      src={optImg} 
                                      alt={`Option choice`} 
                                      style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '8px', border: '1px solid var(--border-light)', marginTop: '4px', cursor: 'zoom-in' }} 
                                      onClick={() => window.open(optImg, '_blank')}
                                    />
                                  </div>
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <div style={{ background: 'var(--bg-main)', borderRadius: '8px', padding: '20px', fontSize: '1rem', color: 'var(--text-main)', border: '1px solid var(--border-light)', display: 'grid', gap: '16px', marginTop: '24px' }}>
                        <div>
                          <strong style={{ display: 'block', marginBottom: '8px' }}>Your answer:</strong>
                          {g ? <MathText key={g} text={g} /> : <em style={{ color: 'var(--text-muted)' }}>No answer provided</em>}
                        </div>
                        {exp.length > 0 && (
                          <div style={{ borderTop: '1px solid var(--border-light)', paddingTop: '16px' }}>
                            <strong style={{ color: 'var(--success)', display: 'block', marginBottom: '8px' }}>Correct Answer(s):</strong>
                            <ul style={{ margin: '4px 0 0', paddingLeft: '18px', color: 'var(--success)' }}>
                              {exp.map((a, i) => <li key={i}><MathText key={a} text={a} /></li>)}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    {qItem.solution && (
                      <div style={{ background: 'var(--bg-main)', borderRadius: '8px', padding: '20px', border: '1px solid var(--border-light)', marginTop: '20px' }}>
                        <strong style={{ display: 'block', marginBottom: '8px', color: 'var(--text-main)' }}>Explanation/Solution:</strong>
                        <div style={{ fontSize: '1rem', lineHeight: '1.6', color: 'var(--text-main)' }}>
                          <MathText text={qItem.solution} />
                        </div>
                      </div>
                    )}

                    <div style={{ borderTop: '1px solid var(--border-light)', paddingTop: '16px', marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
                      <a
                        href={`/question/${examId}/${qItem._id}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => {
                          if (!e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey) {
                            e.preventDefault();
                            setSolutionPopup({ question: qItem, index: idx });
                          }
                        }}
                        className="submit-btn-small"
                        style={{ background: 'var(--primary)', color: 'white', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                      >
                        <i className="fas fa-lightbulb" /> View Solution & Notes
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Floating Navigation Button & Panel */}
          <button 
            className={`floating-map-btn ${showMap ? 'active' : ''}`}
            onClick={() => setShowMap(!showMap)}
            title="Question Map"
          >
            <i className="fas fa-th-large" />
          </button>

          {showMap && (
            <div 
              className="test-nav-panel" 
              style={{ 
                ...panelStyle, 
                display: 'flex', 
                flexDirection: 'column', 
                overflow: 'hidden',
                maxHeight: 'calc(100vh - 200px)' 
              }}
            >
              {isMobileOrTablet && (
                <div 
                  onMouseDown={handleDragStart}
                  onTouchStart={handleDragStart}
                  style={{
                    width: '100%',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'ns-resize',
                    backgroundColor: 'transparent',
                    marginTop: '-12px',
                    marginBottom: '8px',
                    touchAction: 'none'
                  }}
                  className="panel-drag-handle"
                >
                  <div style={{
                    width: '40px',
                    height: '4px',
                    borderRadius: '2px',
                    backgroundColor: 'var(--border-light)',
                  }} />
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Results</span>
                <button 
                  onClick={() => setShowMap(false)}
                  style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0', margin: '0', marginRight: '-12px', width: '18px', height: '18px', minWidth: '0', minHeight: '0', lineHeight: '1' }}
                  title="Close"
                >
                  <i className="fas fa-times" style={{ fontSize: '0.75rem', lineHeight: '1' }} />
                </button>
              </div>

              <div style={{ flex: 1, overflowY: 'auto', paddingRight: '4px', marginTop: '8px' }}>
                <div className="question-grid">
                  {questions.map((q, idx) => {
                    const ans = answers[idx];
                    const ansArr = Array.isArray(ans) ? ans : [];
                    const exp = q.correctAnswers || [];
                    const correct = q.type === 'mcq' && JSON.stringify([...ansArr].sort()) === JSON.stringify([...exp].sort());
                    
                    let dotClass = '';
                    if (q.type === 'mcq') {
                      dotClass = correct ? 'dot-correct' : 'dot-wrong';
                    } else {
                      dotClass = 'dot-open';
                    }

                    return (
                      <button
                        key={idx}
                        type="button"
                        className={`question-dot ${idx === current ? 'dot-current' : ''} ${dotClass}`}
                        onClick={() => {
                          setCurrent(idx);
                          if (viewMode !== 'single') {
                            document.getElementById(`review-q-${idx}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }
                          setShowMap(false);
                        }}
                      >
                        {idx + 1}
                      </button>
                    );
                  })}
                </div>
                
                <div className="nav-legend" style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '16px', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid var(--border-light)', fontSize: '0.75rem' }}>
                  <div className="legend-item" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div className="legend-swatch" style={{ width: '12px', height: '12px', borderRadius: '3px', background: 'var(--success)' }} />
                    <span>Correct</span>
                  </div>
                  <div className="legend-item" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div className="legend-swatch" style={{ width: '12px', height: '12px', borderRadius: '3px', background: 'var(--danger)' }} />
                    <span>Incorrect</span>
                  </div>
                  <div className="legend-item" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div className="legend-swatch" style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#64748b' }} />
                    <span>Open Question</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        {/* Solution Details Modal Popup */}
        {solutionPopup && (
          <div className="modal-overlay" onClick={() => setSolutionPopup(null)}>
            <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: '700px', width: '90%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', padding: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid var(--border-light)' }}>
                <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Question {solutionPopup.index + 1} Solution</h3>
                <button 
                  onClick={() => setSolutionPopup(null)}
                  style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '1.2rem', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '4px' }}
                  title="Close"
                >
                  <i className="fas fa-times" />
                </button>
              </div>

              <div style={{ padding: '24px', overflowY: 'auto', flex: 1, display: 'grid', gap: '20px' }}>
                <div>
                  <strong style={{ display: 'block', marginBottom: '8px', color: 'var(--text-main)' }}>Explanation/Solution:</strong>
                  <div style={{ fontSize: '1rem', lineHeight: '1.6', color: 'var(--text-main)' }}>
                    {solutionPopup.question.solution ? (
                      <MathText text={solutionPopup.question.solution} />
                    ) : (
                      <em style={{ color: 'var(--text-muted)' }}>No explanation available yet.</em>
                    )}
                  </div>
                </div>

                <div style={{ borderTop: '1px solid var(--border-light)', paddingTop: '16px' }}>
                  <strong style={{ display: 'block', marginBottom: '8px', color: 'var(--text-main)' }}>Study Notes:</strong>
                  <div style={{ fontSize: '1rem', lineHeight: '1.6', color: 'var(--text-main)' }}>
                    {solutionPopup.question.notes ? (
                      <MathText text={solutionPopup.question.notes} />
                    ) : (
                      <em style={{ color: 'var(--text-muted)' }}>No notes added yet.</em>
                    )}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', padding: '16px 24px', borderTop: '1px solid var(--border-light)', background: 'var(--bg-card)' }}>
                <a
                  href={`/question/${examId}/${solutionPopup.question._id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="submit-btn-small"
                  style={{ background: 'var(--primary)', color: 'white', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                >
                  <i className="fas fa-external-link-alt" /> Open in New Tab
                </a>
                <button type="button" className="secondary submit-btn-small" onClick={() => setSolutionPopup(null)}>Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ────────────────────────────────────────────
     TEST SCREEN
  ──────────────────────────────────────────── */
  const q = questions[current];
  const isMcq = q?.type === 'mcq';
  const options = q ? (q.options || []) : [];
  const givenArr = Array.isArray(answers[current]) ? answers[current] : [];
  const answered = q ? (isMcq ? givenArr.length > 0 : !!(answers[current]?.trim())) : false;
  const answeredCount = questions.filter((_, idx) => {
    const ans = answers[idx];
    return Array.isArray(ans) ? ans.length > 0 : !!(ans?.trim());
  }).length;
  const progressPct = pct(answeredCount, total);

  // Check if all questions answered
  const allAnswered = questions.every((q, idx) => {
    if (q.type === 'mcq') return Array.isArray(answers[idx]) && answers[idx].length > 0;
    return answers[idx]?.trim();
  });

  return (
    <div 
      className={`test-page-container ${shouldHeaderShow ? 'header-visible' : ''} test-in-progress`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
    >
      <SEO title={`Taking Test: ${exam.title}`} description={`Currently taking the ${exam.title} exam on Study Centro. Stay focused!`} />
      
      {!submitted && (
        <div 
          className="test-header-trigger"
          onMouseEnter={() => setIsHeaderOpen(true)}
        />
      )}

      {!shouldHeaderShow && !submitted && (
        <div className="floating-controls-container">
          {secondsLeft !== null && (
            <div 
              className={`header-timer floating-timer-btn ${secondsLeft <= 60 ? 'timer-danger' : ''}`}
              onClick={() => setIsHeaderOpen(true)}
              title="Click to show controls"
            >
              <i className="fas fa-clock" />
              {String(Math.floor(secondsLeft / 60)).padStart(2, '0')}:{String(secondsLeft % 60).padStart(2, '0')}
            </div>
          )}

          <button 
            className="secondary submit-btn-small floating-timer-btn" 
            onClick={() => {
              setIsHeaderPinned(true);
              setIsHeaderOpen(true);
            }}
            style={{ 
              width: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '6px 12px',
              height: '34px',
              color: 'var(--text-main)'
            }}
            title="Pin Header (Always Visible)"
          >
            <i className="fas fa-thumbtack" style={{ transform: 'rotate(-45deg)' }} />
          </button>
        </div>
      )}

      {/* ── Sticky Header ── */}
      <header 
        className={`test-header ${!shouldHeaderShow ? 'header-hidden' : ''}`}
        onMouseLeave={() => setIsHeaderOpen(false)}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 32px', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button className="secondary submit-btn-small" type="button" onClick={() => setShowExitConfirm(true)}>
              <i className="fas fa-times" /> Exit
            </button>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
              <strong style={{ fontSize: '1rem' }}><MathText key={exam.title} text={exam.title} /></strong>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button 
              className="secondary submit-btn-small" 
              onClick={toggleViewMode}
              style={{ width: 'auto' }}
            >
              <i className={`fas ${viewMode === 'single' ? 'fa-list' : 'fa-pager'}`} style={{ marginRight: '6px' }} />
              {viewMode === 'single' ? 'Show All' : 'Show One by One'}
            </button>

            <button 
              className="secondary submit-btn-small" 
              onClick={() => setIsHeaderPinned(!isHeaderPinned)}
              style={{ 
                width: 'auto', 
                background: isHeaderPinned ? 'var(--primary)' : 'transparent', 
                color: isHeaderPinned ? 'white' : 'var(--text-main)',
                borderColor: isHeaderPinned ? 'var(--primary)' : 'var(--border-light)' 
              }}
              title={isHeaderPinned ? "Unpin Header (Hide on Scroll)" : "Pin Header (Always Visible)"}
            >
              <i className="fas fa-thumbtack" style={{ transform: isHeaderPinned ? 'none' : 'rotate(-45deg)', transition: 'transform 0.2s' }} />
            </button>

            {secondsLeft !== null && (
              <div className={`header-timer ${secondsLeft <= 60 ? 'timer-danger' : ''}`}>
                <i className="fas fa-clock" />
                {String(Math.floor(secondsLeft / 60)).padStart(2, '0')}:{String(secondsLeft % 60).padStart(2, '0')}
              </div>
            )}
            
            <ThemeSwitcher 
              theme={theme} 
              onThemeChange={onThemeChange} 
              compact={true} 
              showLabel={false} 
            />

            <button
              type="button"
              className="submit-btn-small"
              onClick={triggerSubmit}
              style={{ background: 'var(--success)', color: 'white' }}
            >
              <i className="fas fa-check-circle" style={{ marginRight: '6px' }} /> Submit
            </button>
          </div>
        </div>

        {/* Thin Progress Bar just below header content */}
        <div className="test-progress-track">
          <div className="test-progress-fill" style={{ width: `${progressPct}%` }} />
        </div>
      </header>

      <div className="test-body" id="test-body-area">

        {viewMode === 'single' ? (
          /* Main Content Area with Side Nav */
          <div className="test-body-layout">
            <button
              type="button"
              className="nav-side-btn"
              onClick={goPrev}
              disabled={current === 0}
              title="Previous Question"
            >
              <i className="fas fa-chevron-left" />
            </button>

            <div className="test-question-card" style={{ flex: 1, maxWidth: '1000px', margin: '0 auto', minHeight: '400px' }}>
              <div className="test-question-header" style={{ display: 'flex', gap: isMobile ? '0' : '16px', marginBottom: '24px', alignItems: 'flex-start' }}>
                {!isMobile && (
                  <span className="question-number" style={{ flexShrink: 0, marginTop: '2px' }}>{current + 1}</span>
                )}
                <div className="test-question-text" style={{ flex: 1, margin: 0, fontSize: '1.25rem', lineHeight: '1.6', color: 'var(--text-main)', display: 'flex', flexDirection: 'column' }}>
                  <MathText key={q.text} text={isMobile ? `${current + 1}. ${q.text}` : q.text} />
                  {q.imageUrl && (
                    <div style={{ width: '100%', marginBottom: '20px', marginTop: '16px', textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
                      <img 
                        src={q.imageUrl} 
                        alt="Question" 
                        style={{ maxWidth: '100%', maxHeight: '355px', borderRadius: '12px', border: '1px solid var(--border-light)', boxShadow: 'var(--shadow-sm)', cursor: 'zoom-in' }} 
                        onClick={() => window.open(q.imageUrl, '_blank')}
                      />
                    </div>
                  )}
                </div>
              </div>

              {isMcq ? (
                <div style={{ display: 'grid', gap: '12px' }}>

                  {options.map((opt, i) => {
                    const optImg = q.optionImages?.[i];
                    const optIdentifier = opt?.trim() || optImg;
                    if (!optIdentifier) return null;

                    const checked = givenArr.includes(optIdentifier);
                    const isSingle = q.correctAnswers?.length === 1;
                    return (
                      <label
                        key={i}
                        className={`test-option ${checked ? 'test-option-selected' : ''}`}
                      >
                        <div style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: isSingle ? '50%' : '6px',
                          border: '2px solid',
                          borderColor: checked ? 'var(--primary)' : 'var(--border-light)',
                          background: checked ? 'var(--primary)' : 'var(--bg-card)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '0.8rem',
                          flexShrink: 0
                        }}>
                          {checked && <i className={`fas fa-${isSingle ? 'circle' : 'check'}`} style={{ fontSize: isSingle ? '0.5rem' : '0.8rem' }} />}
                        </div>
                        <input
                          type={isSingle ? "radio" : "checkbox"}
                          name={`q-${current}`}
                          checked={checked}
                          onChange={() => isSingle ? selectSingleMcqAnswer(current, optIdentifier) : toggleMcqAnswer(current, optIdentifier)}
                          style={{ display: 'none' }}
                        />
                        <div className="test-option-text" style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
                          {opt?.trim() && <MathText key={opt} text={opt} />}
                          {optImg && (
                            <div style={{ marginTop: '4px' }}>
                              <img 
                                src={optImg} 
                                alt={`Option choice`} 
                                style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '8px', border: '1px solid var(--border-light)', marginTop: '4px', cursor: 'zoom-in' }} 
                                onClick={(e) => { e.stopPropagation(); e.preventDefault(); window.open(optImg, '_blank'); }}
                              />
                            </div>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '16px' }}>
                  <textarea
                    className="test-open-answer"
                    value={answers[current] || ''}
                    onChange={e => setOpenAnswer(current, e.target.value)}
                    placeholder="Write your answer here..."
                  rows={8}
                    style={{ padding: '20px', borderRadius: 'var(--radius-lg)' }}
                  />
                  {answers[current]?.trim() && (
                    <div style={{ padding: '20px', background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-light)' }}>
                      <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '12px' }}>Live Preview</div>
                      <MathText key={answers[current]} text={answers[current]} />
                    </div>
                  )}
                </div>
              )}
            </div>

            <button
              type="button"
              className="nav-side-btn"
              onClick={goNext}
              disabled={current === total - 1}
              title="Next Question"
            >
              <i className="fas fa-chevron-right" />
            </button>
          </div>
        ) : (
          /* Scrollable list of all questions (Test taking view) */
          <div style={{ maxWidth: '1000px', margin: '0 auto 40px', padding: '0 20px', display: 'grid', gap: '20px' }}>
            {questions.map((qItem, idx) => {
              const isMcqQ = qItem.type === 'mcq';
              const opts = (qItem.options || []);
              const givenAns = Array.isArray(answers[idx]) ? answers[idx] : [];
              return (
                <div 
                  key={idx}
                  id={`test-q-${idx}`}
                  className="test-question-card" 
                  style={{ margin: 0, padding: '24px' }}
                >
                  <div className="test-question-header" style={{ display: 'flex', gap: isMobile ? '0' : '16px', marginBottom: '24px', alignItems: 'flex-start' }}>
                    {!isMobile && (
                      <span className="question-number" style={{ flexShrink: 0, marginTop: '2px' }}>{idx + 1}</span>
                    )}
                    <div className="test-question-text" style={{ flex: 1, margin: 0, fontSize: '1.25rem', lineHeight: '1.6', color: 'var(--text-main)', display: 'flex', flexDirection: 'column' }}>
                      <MathText key={qItem.text} text={isMobile ? `${idx + 1}. ${qItem.text}` : qItem.text} />
                      {qItem.imageUrl && (
                        <div style={{ width: '100%', marginBottom: '20px', marginTop: '16px', textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
                          <img 
                             src={qItem.imageUrl} 
                             alt="Question" 
                             style={{ maxWidth: '100%', maxHeight: '355px', borderRadius: '12px', border: '1px solid var(--border-light)', boxShadow: 'var(--shadow-sm)', cursor: 'zoom-in' }} 
                             onClick={() => window.open(qItem.imageUrl, '_blank')}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {isMcqQ ? (
                    <div style={{ display: 'grid', gap: '12px' }}>
                      {opts.map((opt, i) => {
                        const optImg = qItem.optionImages?.[i];
                        const optIdentifier = opt?.trim() || optImg;
                        if (!optIdentifier) return null;
                        const checked = givenAns.includes(optIdentifier);
                        const isSingle = qItem.correctAnswers?.length === 1;
                        return (
                          <label
                            key={i}
                            className={`test-option ${checked ? 'test-option-selected' : ''}`}
                          >
                            <div style={{
                              width: '24px',
                              height: '24px',
                              borderRadius: isSingle ? '50%' : '6px',
                              border: '2px solid',
                              borderColor: checked ? 'var(--primary)' : 'var(--border-light)',
                              background: checked ? 'var(--primary)' : 'var(--bg-card)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontSize: '0.8rem',
                              flexShrink: 0
                            }}>
                              {checked && <i className={`fas fa-${isSingle ? 'circle' : 'check'}`} style={{ fontSize: isSingle ? '0.5rem' : '0.8rem' }} />}
                            </div>
                            <input
                              type={isSingle ? "radio" : "checkbox"}
                              name={`q-${idx}`}
                              checked={checked}
                              onChange={() => isSingle ? selectSingleMcqAnswer(idx, optIdentifier) : toggleMcqAnswer(idx, optIdentifier)}
                              style={{ display: 'none' }}
                            />
                            <div className="test-option-text" style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
                              {opt?.trim() && <MathText key={opt} text={opt} />}
                              {optImg && (
                                <div style={{ marginTop: '4px' }}>
                                  <img 
                                    src={optImg} 
                                    alt={`Option choice`} 
                                    style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '8px', border: '1px solid var(--border-light)', marginTop: '4px', cursor: 'zoom-in' }} 
                                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); window.open(optImg, '_blank'); }}
                                  />
                                </div>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gap: '16px' }}>
                      <textarea
                        className="test-open-answer"
                        value={answers[idx] || ''}
                        onChange={e => setOpenAnswer(idx, e.target.value)}
                        placeholder="Write your answer here..."
                        rows={8}
                        style={{ padding: '20px', borderRadius: 'var(--radius-lg)' }}
                      />
                      {answers[idx]?.trim() && (
                        <div style={{ padding: '20px', background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-light)' }}>
                          <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '12px' }}>Live Preview</div>
                          <MathText key={answers[idx]} text={answers[idx]} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}


        {/* Floating Navigation Button & Panel (Persistent) */}
        <button 
          className={`floating-map-btn ${showMap ? 'active' : ''}`}
          onClick={() => setShowMap(!showMap)}
          title="Question Map"
        >
          <i className="fas fa-th-large" />
        </button>

        {showMap && (
          <div 
            className="test-nav-panel" 
            style={{ 
              ...panelStyle, 
              display: 'flex', 
              flexDirection: 'column', 
              overflow: 'hidden',
              maxHeight: 'calc(100vh - 200px)' 
            }}
          >
            {isMobileOrTablet && (
              <div 
                onMouseDown={handleDragStart}
                onTouchStart={handleDragStart}
                style={{
                  width: '100%',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'ns-resize',
                  backgroundColor: 'transparent',
                  marginTop: '-12px',
                  marginBottom: '8px',
                  touchAction: 'none'
                }}
                className="panel-drag-handle"
              >
                <div style={{
                  width: '40px',
                  height: '4px',
                  borderRadius: '2px',
                  backgroundColor: 'var(--border-light)',
                }} />
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{answeredCount} of {total} answered</span>
              <button 
                onClick={() => setShowMap(false)}
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0', margin: '0', marginRight: '-12px', width: '18px', height: '18px', minWidth: '0', minHeight: '0', lineHeight: '1' }}
                title="Close"
              >
                <i className="fas fa-times" style={{ fontSize: '0.75rem', lineHeight: '1' }} />
              </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', paddingRight: '4px', marginTop: '8px' }}>
              <div className="question-grid">
                {questions.map((_, idx) => {
                  const ans = answers[idx];
                  const done = Array.isArray(ans) ? ans.length > 0 : !!(ans?.trim());
                  return (
                    <button
                      key={idx}
                      type="button"
                      className={`question-dot ${idx === current ? 'dot-current' : done ? 'dot-done' : ''}`}
                      onClick={() => {
                        setCurrent(idx);
                        if (viewMode !== 'single') {
                          document.getElementById(`test-q-${idx}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                        setShowMap(false);
                      }}
                    >
                      {idx + 1}
                    </button>
                  );
                })}
              </div>
              
              <div className="nav-legend" style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '16px', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid var(--border-light)', fontSize: '0.75rem' }}>
                <div className="legend-item" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div className="legend-swatch" style={{ width: '12px', height: '12px', borderRadius: '3px', background: 'var(--primary)' }} />
                  <span>Current</span>
                </div>
                <div className="legend-item" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div className="legend-swatch" style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#10b981', opacity: 0.4 }} />
                  <span>Answered</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        {showConfirm && (
          <div className="modal-overlay" onClick={() => setShowConfirm(false)}>
            <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px', textAlign: 'center' }}>
              <h3>Submit Exam?</h3>
              <p style={{ margin: '16px 0', color: 'var(--text-muted)' }}>Are you sure you want to end the test and see your results?</p>
              <div className="modal-actions" style={{ justifyContent: 'center', gap: '12px' }}>
                <button type="button" onClick={confirmSubmit} style={{ background: 'var(--success)' }}>Yes, Submit</button>
                <button type="button" className="secondary" onClick={() => setShowConfirm(false)}>No, Continue</button>
              </div>
            </div>
          </div>
        )}

        {/* Exit Warning Modal */}
        {showExitConfirm && (
          <div className="modal-overlay" onClick={() => setShowExitConfirm(false)}>
            <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px', textAlign: 'center' }}>
              <h3 style={{ color: 'var(--danger)' }}>Exit Test?</h3>
              <p style={{ margin: '16px 0', color: 'var(--text-muted)' }}>Warning: Your progress will be lost if you exit now. Are you sure?</p>
              <div className="modal-actions" style={{ justifyContent: 'center', gap: '12px' }}>
                <button type="button" onClick={confirmExit} style={{ background: 'var(--danger)' }}>Yes, Exit</button>
                <button type="button" className="secondary" onClick={() => setShowExitConfirm(false)}>No, Stay</button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default TakeTestPage;
